"use client"

import Image, { type ImageLoaderProps } from "next/image"
import { useMemo, useState } from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import {
  canOptimizeImageUrl,
  GAME_PANEL_IMAGE_QUALITY,
  GAME_PANEL_IMAGE_WIDTH,
  GAME_THUMBNAIL_IMAGE_QUALITY,
  GAME_THUMBNAIL_IMAGE_WIDTH,
  getOptimizedImageUrl,
  getProviderSizedImageUrl,
  getReviewedImageUrl,
  getRetryImageUrl,
  normalizeImageUrl,
  shouldLoadProviderImageDirectly,
} from "@/lib/optimized-image"

type GameItemImageFit = "cover" | "contain"
type ImageCandidate = {
  src: string
  unoptimized: boolean
}

interface GameItemImageProps {
  src: string | null | undefined
  sources?: (string | null | undefined)[]
  itemName: string
  gameTypeName?: string
  gameTypeSlug?: string
  categorySlug?: string
  fallbackClassName?: string
  priority?: boolean
  presentation?: "panel" | "thumbnail"
}

const CONTAIN_CONTEXT_PATTERNS = [
  "playing-card",
  "playing card",
  "pokemon-card",
  "pokemon card",
  "steam-peak-players",
  "steam peak players",
  "youtube-subscriber",
  "youtube subscriber",
  "country-population",
  "country population",
  "us-state-population",
  "us state population",
  "internet-speed",
  "internet speed",
  "sports-season-games",
  "sports season games",
]

const CONTAIN_SOURCE_PATTERNS = [
  "flagcdn.com",
  "/flags/",
  "/flag/",
  "playing-cards/",
  "pokemondb.net",
  "pokemoncard",
  "pokemon-card",
  "yt3.ggpht.com",
  "yt3.googleusercontent.com",
  "mzstatic.com",
  "/appicon",
  "/avatar/",
  "/avatars/",
  "/channel-avatar/",
  "/logo/",
  "/logo_",
  "/logos/",
  "/icon/",
  "/icons/",
  "/emblem/",
  "/crest/",
  "/badge/",
]

function includesAny(value: string, patterns: string[]) {
  return patterns.some((pattern) => value.includes(pattern))
}

function gamePanelImageLoader({ src, width, quality }: ImageLoaderProps) {
  const targetWidth = Math.min(width, GAME_PANEL_IMAGE_WIDTH)
  const providerSizedSource = getProviderSizedImageUrl(src, targetWidth)
  if (shouldLoadProviderImageDirectly(providerSizedSource)) {
    return providerSizedSource!
  }

  return (
    getOptimizedImageUrl(src, {
      width: targetWidth,
      quality,
    }) ?? src
  )
}

function gameThumbnailImageLoader({ src, width, quality }: ImageLoaderProps) {
  const targetWidth = Math.min(width, GAME_THUMBNAIL_IMAGE_WIDTH)
  const providerSizedSource = getProviderSizedImageUrl(src, targetWidth)
  if (shouldLoadProviderImageDirectly(providerSizedSource)) {
    return providerSizedSource!
  }

  return (
    getOptimizedImageUrl(src, {
      width: targetWidth,
      quality,
    }) ?? src
  )
}

export function getGameItemImageFit({
  src,
  gameTypeName,
  gameTypeSlug,
  categorySlug,
}: Pick<
  GameItemImageProps,
  "src" | "gameTypeName" | "gameTypeSlug" | "categorySlug"
>): GameItemImageFit {
  const normalizedSource = src?.trim().toLowerCase() ?? ""
  const normalizedContext =
    `${categorySlug ?? ""} ${gameTypeName ?? ""} ${gameTypeSlug ?? ""}`.toLowerCase()
  const sourcePath = normalizedSource.split(/[?#]/, 1)[0]

  if (
    sourcePath.endsWith(".svg") ||
    includesAny(normalizedSource, CONTAIN_SOURCE_PATTERNS) ||
    includesAny(normalizedContext, CONTAIN_CONTEXT_PATTERNS)
  ) {
    return "contain"
  }

  return "cover"
}

function getObjectPosition(gameTypeName?: string, gameTypeSlug?: string) {
  const context = `${gameTypeName ?? ""} ${gameTypeSlug ?? ""}`.toLowerCase()
  if (
    context.includes("actor") ||
    context.includes("celebrity") ||
    context.includes("net worth") ||
    context.includes("nba salar") ||
    context.includes("nfl salar") ||
    context.includes("football market")
  ) {
    return "center 22%"
  }
  if (context.includes("movie") || context.includes("box office") || context.includes("imdb")) {
    return "center 18%"
  }
  return "center"
}

export function GameItemImage({
  src,
  sources,
  itemName,
  gameTypeName,
  gameTypeSlug,
  categorySlug,
  fallbackClassName = "bg-gradient-to-br from-zinc-800 to-zinc-950",
  priority = false,
  presentation = "panel",
}: GameItemImageProps) {
  const gallerySources = useMemo(() => {
    const candidates = [src, ...(sources ?? [])]
      .map((candidate) => candidate?.trim())
      .filter((candidate): candidate is string => Boolean(candidate))
    return [...new Set(candidates)].slice(0, 3)
  }, [sources, src])
  const galleryKey = gallerySources.join("|")
  const [galleryState, setGalleryState] = useState({
    galleryKey: "",
    index: 0,
  })
  const galleryIndex =
    galleryState.galleryKey === galleryKey ? galleryState.index : 0
  const originalSource = gallerySources[galleryIndex] ?? null
  const normalizedSource = normalizeImageUrl(originalSource)
  const directSource = getProviderSizedImageUrl(
    normalizedSource,
    presentation === "thumbnail"
      ? GAME_THUMBNAIL_IMAGE_WIDTH
      : GAME_PANEL_IMAGE_WIDTH
  )
  const retrySource = getRetryImageUrl(normalizedSource)
  const sourceKey = originalSource ?? ""
  const candidates = useMemo(
    () => {
      const nextCandidates: ImageCandidate[] = []
      const addCandidate = (
        candidateSource: string | null | undefined,
        unoptimized: boolean
      ) => {
        if (!candidateSource) return
        if (
          nextCandidates.some(
            (candidate) =>
              candidate.src === candidateSource &&
              candidate.unoptimized === unoptimized
          )
        ) {
          return
        }
        nextCandidates.push({ src: candidateSource, unoptimized })
      }

      if (normalizedSource) {
        const optimizable = canOptimizeImageUrl(normalizedSource)
        addCandidate(
          getReviewedImageUrl(normalizedSource),
          !optimizable
        )
        if (retrySource) {
          addCandidate(getReviewedImageUrl(retrySource), !optimizable)
        }
        // If the self-hosted optimizer cannot reach a provider, make one
        // final direct browser request rather than leaving a blank panel.
        if (optimizable) {
          addCandidate(directSource, true)
        }
      }

      return nextCandidates
    },
    [directSource, normalizedSource, retrySource]
  )
  const [failure, setFailure] = useState({ sourceKey: "", candidateIndex: 0 })
  const candidateIndex = failure.sourceKey === sourceKey ? failure.candidateIndex : 0
  const activeCandidate = candidates[candidateIndex] ?? null
  const activeSource = activeCandidate?.src ?? null
  const fit = getGameItemImageFit({
    src: normalizedSource,
    gameTypeName,
    gameTypeSlug,
    categorySlug,
  })
  // Gate on category alone: every real-estate game type shows a house photo,
  // and some callers (the daily challenge API) never send a game type slug.
  const isHousing =
    categorySlug === "real-estate" || categorySlug === "dutch-houses"
  const isStadium =
    gameTypeSlug === "stadium-capacity" && categorySlug === "sports"
  const objectPosition = getObjectPosition(gameTypeName, gameTypeSlug)
  const sizes =
    presentation === "thumbnail"
      ? "(min-width: 1280px) 360px, (min-width: 768px) 42vw, 92vw"
      : "(min-width: 768px) 50vw, 100vw"
  const quality =
    presentation === "thumbnail"
      ? GAME_THUMBNAIL_IMAGE_QUALITY
      : GAME_PANEL_IMAGE_QUALITY
  const imageLoader =
    presentation === "thumbnail"
      ? gameThumbnailImageLoader
      : gamePanelImageLoader
  const imageLoadingProps = priority
    ? { preload: true as const, fetchPriority: "high" as const }
    : { loading: "lazy" as const, fetchPriority: "auto" as const }

  const handleError = () => {
    if (candidateIndex + 1 < candidates.length) {
      setFailure({
        sourceKey,
        candidateIndex: candidateIndex + 1,
      })
      return
    }

    // A game item can have several reviewed photos. If one source and all of
    // its delivery fallbacks fail, automatically try the next photo instead
    // of leaving the whole panel blank.
    if (galleryIndex + 1 < gallerySources.length) {
      setGalleryState({
        galleryKey,
        index: galleryIndex + 1,
      })
      setFailure({ sourceKey: "", candidateIndex: 0 })
      return
    }

    setFailure({
      sourceKey,
      candidateIndex: candidateIndex + 1,
    })
  }

  const handleLoad = () => {
    // Remember successful source changes as well as failures. Without this,
    // returning to an image that once failed could incorrectly skip every
    // candidate for the rest of the game session.
    setFailure((previous) =>
      previous.sourceKey === sourceKey
        ? previous
        : { sourceKey, candidateIndex }
    )
  }

  const moveGallery = (direction: -1 | 1) => {
    setGalleryState({
      galleryKey,
      index:
        (galleryIndex + direction + gallerySources.length) %
        gallerySources.length,
    })
  }

  return (
    <div
      className={`absolute inset-0 overflow-hidden ${fallbackClassName}`}
      data-image-fit={fit}
    >
      <div
        className="absolute inset-0 flex items-center justify-center overflow-hidden pointer-events-none select-none"
        aria-hidden="true"
      >
        <span
          className={`max-w-full truncate px-4 font-black leading-none text-center text-white/[0.035] ${
            presentation === "thumbnail"
              ? "text-5xl md:text-7xl"
              : "text-[8rem] md:text-[12rem]"
          }`}
        >
          {itemName.split(" ")[0]}
        </span>
      </div>

      {!activeSource && (
        <div
          className="absolute bottom-3 right-3 z-[1] rounded-full border border-white/15 bg-black/35 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-white/55 backdrop-blur-sm"
          role="img"
          aria-label={`Image unavailable for ${itemName}`}
        >
          Image unavailable
        </div>
      )}

      {activeSource && fit === "contain" && (
        <>
          <Image
            key={`backdrop-${activeSource}`}
            src={activeSource}
            alt=""
            fill
            unoptimized={activeCandidate.unoptimized}
            loader={imageLoader}
            sizes={sizes}
            quality={quality}
            className="scale-110 object-cover opacity-35 blur-2xl"
            style={{ objectPosition }}
            aria-hidden="true"
            draggable={false}
          />
          <div className="absolute inset-0 bg-zinc-950/45" aria-hidden="true" />
          <Image
            key={`subject-${activeSource}`}
            src={activeSource}
            alt={itemName}
            fill
            unoptimized={activeCandidate.unoptimized}
            loader={imageLoader}
            sizes={sizes}
            quality={quality}
            className={`object-contain opacity-90 ${
              presentation === "thumbnail" ? "p-3 md:p-4" : "p-7 md:p-12"
            }`}
            style={{ objectPosition: "center" }}
            {...imageLoadingProps}
            draggable={false}
            onLoad={handleLoad}
            onError={handleError}
          />
        </>
      )}

      {activeSource && fit === "cover" && (
        <Image
          key={`subject-${activeSource}`}
          src={activeSource}
          alt={itemName}
          fill
          unoptimized={activeCandidate.unoptimized}
          loader={imageLoader}
          sizes={sizes}
          quality={quality}
          className={`animate-housing-gallery-in object-cover ${
            isHousing
              ? "brightness-[1.12] contrast-[0.98] saturate-[1.03]"
              : isStadium
                ? "brightness-[1.08] contrast-[1.02] saturate-[1.04]"
              : ""
          }`}
          style={{ objectPosition }}
          {...imageLoadingProps}
          draggable={false}
          onLoad={handleLoad}
          onError={handleError}
        />
      )}

      {gallerySources.length > 1 && presentation === "panel" && (
        <div
          className="absolute right-3 top-3 z-20 flex items-center gap-1 rounded-full border border-white/20 bg-black/55 p-1 text-white shadow-lg backdrop-blur-md md:right-5 md:top-5"
          aria-label={`${itemName} photo gallery`}
        >
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation()
              moveGallery(-1)
            }}
            className="grid size-8 place-items-center rounded-full transition-colors hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            aria-label={`Show previous photo of ${itemName}`}
          >
            <ChevronLeft className="size-4" aria-hidden="true" />
          </button>
          <span
            className="min-w-10 text-center text-[11px] font-bold tabular-nums"
            aria-live="polite"
          >
            {galleryIndex + 1} / {gallerySources.length}
          </span>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation()
              moveGallery(1)
            }}
            className="grid size-8 place-items-center rounded-full transition-colors hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            aria-label={`Show next photo of ${itemName}`}
          >
            <ChevronRight className="size-4" aria-hidden="true" />
          </button>
        </div>
      )}

      <div
        className={`absolute inset-0 bg-gradient-to-b ${
          isHousing
            ? presentation === "thumbnail"
              ? "from-transparent via-transparent to-black/20"
              : "from-transparent via-transparent to-black/10"
            : isStadium
              ? presentation === "thumbnail"
                ? "from-black/10 via-black/15 to-black/45"
                : "from-black/25 via-black/30 to-black/58"
            : fit === "contain"
            ? presentation === "thumbnail"
              ? "from-black/10 via-black/15 to-black/55"
              : "from-black/40 via-black/55 to-black/85"
            : presentation === "thumbnail"
              ? "from-black/20 via-black/25 to-black/55"
              : "from-black/55 via-black/60 to-black/85"
        }`}
        aria-hidden="true"
      />
    </div>
  )
}
